import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { 
    getMyStore,
    getShippingMethods,
    getPaymentMethods,
    getStoreShippingMethodsConfig,
    updateStoreShippingMethods,
    updateStorePaymentMethods,
    getMyStorePaymentConfigs
} from '../../services/supabaseService';

export const StoreShippingPayment: React.FC = () => {
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [store, setStore] = useState<any>(null);

    const [allShippingMethods, setAllShippingMethods] = useState<any[]>([]);
    const [allPaymentMethods, setAllPaymentMethods] = useState<any[]>([]);
    const [selectedShippingMethods, setSelectedShippingMethods] = useState<number[]>([]);
    const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<number[]>([]);

    useEffect(() => {
        if (user && user.role_id === 6) {
            fetchData();
        } else if (user) {
            navigate('/');
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const myStore = await getMyStore();
            if (myStore) {
                setStore(myStore);

                const [shippingData, storeShippingConfig, paymentData, payConfigRes] = await Promise.all([
                    getShippingMethods(),
                    getStoreShippingMethodsConfig(myStore.id),
                    getPaymentMethods(),
                    getMyStorePaymentConfigs(myStore.id)
                ]);

                setAllShippingMethods(shippingData || []);
                setSelectedShippingMethods((storeShippingConfig || []).map((item: any) => item.shipping_method_id));

                setAllPaymentMethods(paymentData || []);
                if (payConfigRes && !payConfigRes.error && payConfigRes.data) {
                    setSelectedPaymentMethods(payConfigRes.data.map((item: any) => item.payment_method_id));
                }
            }
        } catch (error) {
            showNotification("Erro ao carregar opções de frete e pagamento", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleShipping = (methodId: number) => {
        if (selectedShippingMethods.includes(methodId)) {
            setSelectedShippingMethods(selectedShippingMethods.filter(id => id !== methodId));
        } else {
            setSelectedShippingMethods([...selectedShippingMethods, methodId]);
        }
    };

    const handleTogglePayment = (methodId: number) => {
        if (selectedPaymentMethods.includes(methodId)) {
            setSelectedPaymentMethods(selectedPaymentMethods.filter(id => id !== methodId));
        } else {
            setSelectedPaymentMethods([...selectedPaymentMethods, methodId]);
        }
    };

    const handleSave = async () => {
        if (!store) return;
        setSaving(true);
        try {
            const shipSuccess = await updateStoreShippingMethods(store.id, selectedShippingMethods);
            const paySuccess = await updateStorePaymentMethods(store.id, selectedPaymentMethods);
            
            if (shipSuccess && paySuccess) {
                showNotification("Opções de frete e pagamentos salvas com sucesso!", "success");
            } else {
                showNotification("Ocorreu um erro ao salvar algumas configurações.", "error");
            }
        } catch (error) {
            showNotification("Erro ao salvar opções de frete e pagamentos", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-12 text-center text-slate-500 font-bold uppercase tracking-widest text-xs animate-pulse">
                Carregando opções de frete e pagamento...
            </div>
        );
    }

    if (!store) {
        return (
            <div className="p-12 text-center text-slate-400 font-bold text-sm">
                Loja não encontrada ou permissão insuficiente.
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
                <div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400 mb-1">
                        <Link to="/minha-loja" className="hover:text-purple-400 transition-colors">Configurações</Link>
                        <span>/</span>
                        <span className="text-white">Frete & Pagamento</span>
                    </div>
                    <h1 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                        <i className="fas fa-truck-fast text-purple-500 text-xl"></i>
                        Frete & Pagamento
                    </h1>
                    <p className="text-slate-400 text-xs mt-0.5">
                        Defina as modalidades de envio e as opções de pagamento oferecidas aos clientes no fechamento de pedidos.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black px-5 py-2 rounded-xl text-xs uppercase tracking-wider transition-all shadow-md shadow-purple-600/20 active:scale-95 flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <i className="fas fa-spinner fa-spin text-xs"></i>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check text-xs"></i>
                                Salvar Alterações
                            </>
                        )}
                    </button>
                </div>
            </header>

            {/* Mercado Pago Highlight Card */}
            <div className="bg-gradient-to-r from-blue-950/40 via-slate-900 to-slate-900 border border-blue-500/30 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400 shrink-0">
                        <i className="fas fa-wallet text-base"></i>
                    </div>
                    <div>
                        <h2 className="text-xs font-bold text-white uppercase tracking-wider">Integração Mercado Pago</h2>
                        <p className="text-xs text-slate-400 mt-0.5">
                            Receba pagamentos com QR Code Pix dinâmico e conciliação automática diretamente na sua conta.
                        </p>
                    </div>
                </div>
                <Link
                    to="/minha-loja/mercadopago"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shrink-0 flex items-center gap-2"
                >
                    Configurar Mercado Pago
                    <i className="fas fa-arrow-right text-[10px]"></i>
                </Link>
            </div>

            {/* Shipping Methods Section */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-7 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-800/80">
                    <div>
                        <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                            <span className="w-1.5 h-4 bg-purple-500 rounded-full"></span>
                            Modalidades de Frete & Envio
                        </h2>
                        <p className="text-slate-400 text-xs mt-0.5">
                            Selecione as opções de entrega que sua loja disponibiliza.
                        </p>
                    </div>
                    <span className="text-[11px] font-bold text-slate-400">
                        {selectedShippingMethods.length} selecionadas
                    </span>
                </div>

                {allShippingMethods.length === 0 ? (
                    <div className="text-center py-8 bg-slate-950/40 rounded-xl border border-dashed border-slate-800">
                        <i className="fas fa-truck-fast text-2xl text-slate-700 mb-2 block"></i>
                        <p className="text-slate-400 text-xs font-bold">Nenhum método de frete disponível no sistema.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
                        {allShippingMethods.map(method => {
                            const isSelected = selectedShippingMethods.includes(method.id);
                            return (
                                <button
                                    key={method.id}
                                    type="button"
                                    onClick={() => handleToggleShipping(method.id)}
                                    className={`p-4 rounded-2xl border text-left transition-all duration-200 flex items-center gap-3.5 ${
                                        isSelected 
                                            ? 'bg-purple-500/10 border-purple-500/70 shadow-md shadow-purple-500/5' 
                                            : 'bg-slate-950/50 border-slate-800 hover:border-slate-700 hover:bg-slate-950/80'
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                                        isSelected 
                                            ? 'bg-purple-600/20 border-purple-500/40 text-purple-400' 
                                            : 'bg-slate-900 border-slate-800 text-slate-500'
                                    }`}>
                                        <i className="fas fa-truck-fast text-base"></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-bold uppercase tracking-wider truncate transition-colors ${
                                            isSelected ? 'text-white' : 'text-slate-400'
                                        }`}>
                                            {method.company_name || 'Transportadora'}
                                        </p>
                                        <p className="text-[11px] text-slate-400 font-semibold mt-0.5 truncate">{method.name || method.service_type}</p>
                                    </div>
                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                                        isSelected 
                                            ? 'bg-purple-600 border-purple-500 text-white' 
                                            : 'border-slate-800 bg-slate-950'
                                    }`}>
                                        {isSelected && <i className="fas fa-check text-[9px] font-black"></i>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Payment Methods Section */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-7 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-800/80">
                    <div>
                        <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                            <span className="w-1.5 h-4 bg-emerald-500 rounded-full"></span>
                            Meios de Pagamento Aceitos
                        </h2>
                        <p className="text-slate-400 text-xs mt-0.5">
                            Marque as formas de pagamento aceitas em compras online ou balcão.
                        </p>
                    </div>
                    <span className="text-[11px] font-bold text-slate-400">
                        {selectedPaymentMethods.length} selecionados
                    </span>
                </div>

                {allPaymentMethods.length === 0 ? (
                    <div className="text-center py-8 bg-slate-950/40 rounded-xl border border-dashed border-slate-800">
                        <i className="fas fa-qrcode text-2xl text-slate-700 mb-2 block"></i>
                        <p className="text-slate-400 text-xs font-bold">Nenhum meio de pagamento cadastrado no sistema.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
                        {allPaymentMethods.map(method => {
                            const isSelected = selectedPaymentMethods.includes(method.id);
                            const isPix = method.slug?.includes('pix') || method.name?.toLowerCase().includes('pix');
                            const isCredit = method.slug?.includes('cartao') || method.name?.toLowerCase().includes('cart') || method.name?.toLowerCase().includes('crédit');
                            const icon = isPix ? 'fa-qrcode' : (isCredit ? 'fa-credit-card' : 'fa-money-bill-transfer');

                            return (
                                <button
                                    key={method.id}
                                    type="button"
                                    onClick={() => handleTogglePayment(method.id)}
                                    className={`p-4 rounded-2xl border text-left transition-all duration-200 flex items-center gap-3.5 ${
                                        isSelected 
                                            ? 'bg-purple-500/10 border-purple-500/70 shadow-md shadow-purple-500/5' 
                                            : 'bg-slate-950/50 border-slate-800 hover:border-slate-700 hover:bg-slate-950/80'
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                                        isSelected 
                                            ? 'bg-purple-600/20 border-purple-500/40 text-purple-400' 
                                            : 'bg-slate-900 border-slate-800 text-slate-500'
                                    }`}>
                                        <i className={`fas ${icon} text-base`}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-bold uppercase tracking-wider truncate transition-colors ${
                                            isSelected ? 'text-white' : 'text-slate-400'
                                        }`}>
                                            {method.name || 'Método de Pagamento'}
                                        </p>
                                        <p className="text-[10px] text-slate-500 font-mono mt-0.5">{method.slug}</p>
                                    </div>
                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                                        isSelected 
                                            ? 'bg-purple-600 border-purple-500 text-white' 
                                            : 'border-slate-800 bg-slate-950'
                                    }`}>
                                        {isSelected && <i className="fas fa-check text-[9px] font-black"></i>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="pt-4 border-t border-slate-800 flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black px-8 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-md shadow-purple-600/20 active:scale-95 flex items-center gap-2"
                    >
                        {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                </div>
            </div>
        </div>
    );
};
